import React from 'react';
import { AlertTriangle, Mail, MessageSquare } from 'lucide-react';

/**
 * Inline amber banner shown above an admin toggle when the channel that
 * toggle depends on (email or SMS) is not yet configured. Renders nothing
 * when the channel is configured.
 *
 * Props:
 *   - channel: 'email' | 'sms'
 *   - reason: string from useCommPreflight()
 *   - featureName: short label for the feature being gated, e.g. "down payments"
 *   - onConfigureClick: optional () => void; if provided, renders a "Configure now" button
 */
export default function CommPreflightWarning({ channel, reason, featureName, onConfigureClick }) {
  if (!reason) return null;
  const Icon = channel === 'sms' ? MessageSquare : Mail;
  const channelLabel = channel === 'sms' ? 'SMS' : 'email';

  return (
    <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3 mb-3">
      <div className="flex items-start space-x-2">
        <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={18} />
        <div className="flex-1 text-sm">
          <p className="font-semibold text-amber-900">
            <Icon size={14} className="inline mr-1 -mt-0.5" />
            {channelLabel.charAt(0).toUpperCase() + channelLabel.slice(1)} is not configured
          </p>
          <p className="text-amber-800 mt-1">
            {featureName ? <>You can't enable <strong>{featureName}</strong> until {channelLabel} is set up. </> : null}
            {reason}
          </p>
          {onConfigureClick && (
            <button
              type="button"
              onClick={onConfigureClick}
              className="mt-2 text-xs font-semibold text-amber-900 underline hover:text-amber-700"
            >
              Configure {channelLabel} settings →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
